/**
 * /w/:wsId/settings — workspace settings (FRIDAY-43, FRIDAY-65).
 *
 * The screen is a tabbed shell. The tabs are ROUTES, so the URL is the
 * source of truth: a direct link, a refresh, the browser back/forward
 * buttons and a workspace switch all land on a valid tab.
 *
 *   /w/:wsId/settings              → General (default list columns)
 *   /w/:wsId/settings/members      → Members + pending invites
 *   /w/:wsId/settings/labels       → Labels of every board
 *   /w/:wsId/settings/milestones   → Milestones of every board
 *   /w/:wsId/settings/boards       → The boards of the workspace
 *   /w/:wsId/settings/pages        → Archived pages (FRIDAY-69)
 *
 * Adding a tab is one route (see `routes.tsx`) plus one entry in
 * `WORKSPACE_SETTINGS_TABS` below.
 *
 * The General tab owns the workspace's default list columns. The tier itself
 * lives in `list_view_preferences`; the API is shared with the board settings
 * screen (see `features/settings/listDefaults.ts`).
 */

import { useParams, useNavigate, useLocation, Outlet } from '@tanstack/react-router'
import { Tabs } from '@/components/ui/Tabs'
import { useAuth } from '@/features/auth/authContext'
import { useWorkspaceMembers } from '@/features/workspaces/workspaces'
import { useSaveWorkspaceListDefaults, useWorkspaceListDefaults } from '@/features/settings/listDefaults'
import { useListDefaultsDraft } from '@/features/settings/useListDefaultsDraft'
import { ListFieldsEditor } from '@/components/settings/ListFieldsEditor'
import { NavigationGuardProvider } from '@/features/settings/useUnsavedChangesGuard'
import { CloseSettingsButton } from '@/components/settings/CloseSettingsButton'
import { DEFAULT_LIST_FIELDS } from '@friday/shared/listFields'
import { WorkspaceMembersPanel } from '@/components/workspace/WorkspaceMembersPanel'
import { WorkspaceLabelsPanel } from '@/components/workspace/WorkspaceLabelsPanel'
import { WorkspaceMilestonesPanel } from '@/components/workspace/WorkspaceMilestonesPanel'
import { WorkspaceBoardsPanel } from '@/components/workspace/WorkspaceBoardsPanel'
import { WorkspacePagesPanel } from '@/components/workspace/WorkspacePagesPanel'

/**
 * The tab strip. `to` is the route the tab lives at — adding a tab is one
 * entry here plus one route in `routes.tsx`.
 */
export const WORKSPACE_SETTINGS_TABS = [
  { id: 'general', label: 'General', to: '/w/$wsId/settings' },
  { id: 'members', label: 'Members', to: '/w/$wsId/settings/members' },
  { id: 'labels', label: 'Labels', to: '/w/$wsId/settings/labels' },
  { id: 'milestones', label: 'Milestones', to: '/w/$wsId/settings/milestones' },
  { id: 'boards', label: 'Boards', to: '/w/$wsId/settings/boards' },
  { id: 'pages', label: 'Pages', to: '/w/$wsId/settings/pages' },
] as const

const TABS_ID_BASE = 'workspace-settings'

/**
 * The layout route of the settings area. It owns the tab strip; every tab
 * renders its own panel through the router outlet.
 */
export function WorkspaceSettingsLayout() {
  const { wsId } = useParams({ strict: false }) as { wsId: string }
  const navigate = useNavigate()
  const location = useLocation()
  const activeId = activeTabId(location.pathname, wsId)

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <Tabs
            tabs={WORKSPACE_SETTINGS_TABS.map((t) => ({ id: t.id, label: t.label }))}
            activeId={activeId}
            label="Workspace settings sections"
            idBase={TABS_ID_BASE}
            onSelect={(id) => {
              const tab = WORKSPACE_SETTINGS_TABS.find((t) => t.id === id)
              if (!tab) return
              navigate({ to: tab.to, params: { wsId } })
            }}
          />
          <CloseSettingsButton fallback={`/w/${wsId}`} />
        </div>
        <div className="space-y-4">
          <Outlet />
        </div>
      </div>
    </div>
  )
}

/**
 * Which tab the current URL selects. The URL is the source of truth, so a
 * refresh or a back/forward navigation always lands on the same tab. An
 * unknown suffix falls back to the default tab instead of rendering an
 * empty screen.
 */
function activeTabId(pathname: string, wsId: string): string {
  const prefix = `/w/${wsId}/settings`
  const rest = pathname.startsWith(prefix)
    ? pathname.slice(prefix.length).replace(/^\//, '').replace(/\/+$/, '')
    : ''
  const match = WORKSPACE_SETTINGS_TABS.find((t) => t.to === `/w/$wsId/settings${rest ? `/${rest}` : ''}`)
  return match ? match.id : 'general'
}

export function WorkspaceSettingsView() {
  const { wsId } = useParams({ strict: false }) as { wsId: string }
  return (
    <NavigationGuardProvider>
      <WorkspaceSettingsScreen wsId={wsId} />
    </NavigationGuardProvider>
  )
}

export function WorkspaceSettingsScreen({ wsId }: { wsId: string }) {
  const { user } = useAuth()
  const defaults = useWorkspaceListDefaults(wsId)
  const members = useWorkspaceMembers(wsId)
  const save = useSaveWorkspaceListDefaults(wsId)

  // The edit session (draft + the version it started from) lives in a shared
  // hook so the board columns behave identically.
  const draft = useListDefaultsDraft(defaults.data, defaults.refetch, save, 'inherits the application default')

  const role = members.data?.find((m) => m.userId === user?.id)?.role
  // The server gate for this PUT is "workspace owner or admin".
  const canEdit = role === 'owner' || role === 'admin'

  // The workspace tier's own row. null = no row = inherit the application
  // default (the same `?? DEFAULT_LIST_FIELDS` fallback the editor shows).
  const ownFields = defaults.data?.layers.workspace.fields ?? null

  // Wait for the member list as well: it carries the caller's role, and
  // rendering the editor read-only before it arrives would flash the wrong
  // state.
  if (defaults.isLoading || members.isLoading) {
    return (
      <div className="text-surface-500 flex h-full items-center justify-center text-sm">
        Loading workspace settings…
      </div>
    )
  }

  if (defaults.isError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-500">
        Could not load the workspace settings.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold">Workspace settings</h1>
        <p className="text-surface-500 mt-1 text-xs">
          These columns are the default for every board in this workspace. A board can override them.
        </p>
      </header>

      <div data-testid="workspace-defaults">
        <ListFieldsEditor
          label="Workspace default columns"
          subject="workspace"
          value={ownFields}
          pending={draft.pending}
          inheritedValue={DEFAULT_LIST_FIELDS}
          source="application"
          canEdit={canEdit}
          readOnlyReason="Only workspace owners and admins can change the default columns."
          saving={save.isPending}
          error={draft.error}
          dirty={draft.dirty}
          conflict={draft.conflict}
          notice={draft.staleNote}
          onChange={draft.onChange}
          onSave={() => void draft.save('Could not save the default columns.')}
          onCancel={draft.cancel}
          onResetToInherit={() => void draft.reset('Could not reset the default columns.')}
        />
      </div>
    </div>
  )
}

/** Members tab — shares its implementation with the `/w/:wsId/share` screen. */
export function WorkspaceSettingsMembersView() {
  const { wsId } = useParams({ strict: false }) as { wsId: string }
  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold">Members</h1>
        <p className="text-surface-500 mt-1 text-xs">
          Who can see this workspace and what they can do there.
        </p>
      </header>
      <WorkspaceMembersPanel wsId={wsId} />
    </div>
  )
}

/** Labels tab — every board's labels in one place. */
export function WorkspaceSettingsLabelsView() {
  const { wsId } = useParams({ strict: false }) as { wsId: string }
  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold">Labels</h1>
        <p className="text-surface-500 mt-1 text-xs">
          Labels live on a board. This list shows them all together.
        </p>
      </header>
      <WorkspaceLabelsPanel wsId={wsId} />
    </div>
  )
}

/** Milestones tab — every board's milestones in one place. */
export function WorkspaceSettingsMilestonesView() {
  const { wsId } = useParams({ strict: false }) as { wsId: string }
  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold">Milestones</h1>
        <p className="text-surface-500 mt-1 text-xs">
          Milestones live on a board. This list shows them all together.
        </p>
      </header>
      <WorkspaceMilestonesPanel wsId={wsId} />
    </div>
  )
}

/** Boards tab — the workspace's boards and their settings. */
export function WorkspaceSettingsBoardsView() {
  const { wsId } = useParams({ strict: false }) as { wsId: string }
  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold">Boards</h1>
        <p className="text-surface-500 mt-1 text-xs">
          Every board of this workspace, with a way into its own settings.
        </p>
      </header>
      <WorkspaceBoardsPanel wsId={wsId} />
    </div>
  )
}

/** Pages tab — the archived pages, with restore and permanent delete. */
export function WorkspaceSettingsPagesView() {
  const { wsId } = useParams({ strict: false }) as { wsId: string }
  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold">Pages</h1>
        <p className="text-surface-500 mt-1 text-xs">
          The sidebar shows the active pages. Archived pages wait here until they are restored.
        </p>
      </header>
      <WorkspacePagesPanel wsId={wsId} />
    </div>
  )
}
